/**
 * What the user is told about video on a call, in a toast: what went wrong - the call stays on audio (spec §10.4) - and
 * which camera the call uses when it isn't the one chosen
 */

import { watch } from 'vue'
import _i18n from '@/utilities/I18n'
import type { CallVideoState, useVideoCallsStore } from '@/stores/VideoCalls'
import type { ToastMessage } from '@/definitions/Common'

const i18n = _i18n.global

/** What can go wrong with video on a call, as SipVideo records it */
export type VideoError = NonNullable<CallVideoState['error']>

const ERROR_TEXT: Record<VideoError, string> = {
	permission: 'mycalls.videoCameraDenied',
	busy: 'mycalls.videoCameraBusy', // on Windows usually another program - often Teams - holding the camera
	notfound: 'mycalls.videoCameraNotFound',
	rejected: 'mycalls.videoNotAvailable', // refused for good on this call - the camera button stays, disabled
	// A passing failure - glare that outlasted the retry (491), a server error (5xx), no answer, or the video transport
	// failing - says nothing about the call, so the camera can be tried again
	media: 'mycalls.videoCouldNotStart',
	screen: 'mycalls.videoScreenShareFailed' // capturing the screen failed - cancelling the picker is not an error
}

/**
 * The toast for something that went wrong with video
 * @param error What went wrong
 */
export function videoErrorNotice(error: VideoError): ToastMessage {
	return { severity: 'warn', summary: i18n.t('settings.phone.camera'), detail: i18n.t(ERROR_TEXT[error]) }
}

/**
 * The toast for a camera turned on that isn't the one chosen, because that one is missing
 * @param missing The name of the camera chosen
 * @param using The name of the camera used instead
 */
export function cameraFallbackNotice(missing: string, using: string): ToastMessage {
	const detail = i18n.t('sipClient.videoDeviceUnavailableInsteadUsing').replace('%%old%%', missing).replace('%%new%%', using)

	return { severity: 'info', summary: i18n.t('sipClient.videoDeviceChangedHeader'), detail }
}

/**
 * The toast for a call moved to another camera
 * @param using The name of the camera the call uses now
 */
export function cameraNowUsingNotice(using: string): ToastMessage {
	return { severity: 'info', summary: i18n.t('sipClient.videoDeviceChangedHeader'), detail: i18n.t('sipClient.videoDeviceNowUsing').replace('%%new%%', using) }
}

/**
 * Call back once for each new error on a call's video - not again while it stays, and again after another try failed
 * @param store The video calls store
 * @param onError Receives the call id and the error
 * @returns Stops watching
 */
export function watchVideoErrors(store: ReturnType<typeof useVideoCallsStore>, onError: (callId: string, error: VideoError) => void) {
	return watch(() => Object.values(store.calls).map(video => [ video.callId, video.error ] as const), (errors, previous) => {
		const before = new Map(previous)

		for (const [ callId, error ] of errors) {
			if (error && before.get(callId) !== error) { onError(callId, error) }
		}
	})
}
